import { Link } from 'react-router-dom';
import {
  ArrowRight,
  Calculator,
  FlaskConical,
  Atom,
  Microscope,
  BookOpen,
  Briefcase,
  Scale,
  Brain,
  Landmark,
  Languages,
  GraduationCap,
  Globe,
  PenTool,
} from 'lucide-react';

const getSubjectIcon = (name = '') => {
  const n = name.toLowerCase();
  if (n.includes('math')) return Calculator;
  if (n.includes('account')) return Calculator;
  if (n.includes('chem')) return FlaskConical;
  if (n.includes('phys')) return Atom;
  if (n.includes('bio')) return Microscope;
  if (n.includes('econ')) return Landmark;
  if (n.includes('business')) return Briefcase;
  if (n.includes('law')) return Scale;
  if (n.includes('psych')) return Brain;
  if (n.includes('english')) return PenTool;
  if (n.includes('lang') || n.includes('french') || n.includes('spanish')) return Languages;
  if (n.includes('geo')) return Globe;
  if (n.includes('history') || n.includes('ict') || n.includes('computer')) return BookOpen;
  return GraduationCap;
};

/**
 * Subject card — original red-left-border style with pill-shaped icon container.
 *
 * Props:
 *   subject        — subject row from Supabase
 *   to             — route e.g. `/a-levels/${slug}` or `/igcse/${slug}`
 *   showExamBoard  — show exam board badge (true for IGCSE where it varies)
 */
const SubjectCard = ({ subject, to, showExamBoard = false }) => {
  const Icon = getSubjectIcon(subject.name);
  // Split "Cambridge & Edexcel" → ['Cambridge', 'Edexcel']
  const examBoards = subject.exam_board
    ? subject.exam_board.split('&').map(b => b.trim()).filter(Boolean)
    : [];

  return (
    <Link
      to={to}
      data-testid={`subject-card-${subject.slug}`}
      className="group relative overflow-hidden border-l-4 border-aspire-red bg-white shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all duration-200 cursor-pointer p-4 sm:p-5 flex flex-col"
    >
      {/* Top row: pill icon + optional exam board badges */}
      <div className="flex justify-between items-start mb-3 sm:mb-4">
        {/* Pill-shaped icon container */}
        <div className="inline-flex items-center justify-center bg-aspire-red/10 text-aspire-red rounded-full px-4 py-2 group-hover:bg-aspire-red group-hover:text-white transition-colors duration-200">
          <Icon className="w-4 h-4 sm:w-5 sm:h-5" strokeWidth={1.75} />
        </div>

        {showExamBoard && examBoards.length > 0 && (
          <div className="flex flex-col gap-1 items-end">
            {examBoards.map(board => (
              <span key={board} className="text-xs font-mono font-medium bg-gray-100 px-2 py-0.5 text-gray-500 rounded">
                {board}
              </span>
            ))}
          </div>
        )}
      </div>

      {/* Subject name */}
      <h3 className="font-heading font-bold text-base sm:text-lg text-charcoal group-hover:text-aspire-red transition-colors leading-tight">
        {subject.name}
      </h3>

      {/* Spacer */}
      <div className="flex-1" />

      {/* View details */}
      <div className="mt-4 flex items-center gap-1.5 text-aspire-red font-bold text-xs sm:text-sm group-hover:gap-2.5 transition-all duration-200">
        View Details
        <ArrowRight className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
      </div>
    </Link>
  );
};

export default SubjectCard;
